import { RouterProvider, createRouter } from "@tanstack/react-router"
import { type ReactElement } from "react"
import { Provider as UrqlProvider, cacheExchange, createClient, fetchExchange } from "urql"

import { routeTree } from "../../routeTree.gen"

import { useAuthStore } from "@/stores/authStore"
import { urqlAuthExchange } from "@/utils/auth"
import { getApiOrigin } from "@/utils/getApiOrigin"

// Define router context type
export interface RouterContext {
  auth: ReturnType<typeof useAuthStore.getState>
  queryClient: ReturnType<typeof createUrqlClient>
  cachelessQueryClient: ReturnType<typeof createUrqlClient>
}

// Create URQL client
const createUrqlClient = (disableCache = true) =>
  createClient({
    url: getApiOrigin(),
    exchanges: disableCache ? [urqlAuthExchange, fetchExchange] : [cacheExchange, urqlAuthExchange, fetchExchange],
  })

// Create router with proper context type
export const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    queryClient: undefined!,
    cachelessQueryClient: undefined!,
  } as RouterContext,
  defaultPreload: "intent",
  defaultPreloadDelay: 0,
})

// Register router type
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

export const queryClient = createUrqlClient()

export const cachelessQueryClient = createUrqlClient(true)

// TODO: https://robotsandpencils.atlassian.net/browse/AK-2965 restore using cached query client across the app

export const cachedQueryClient = createUrqlClient(false)

useAuthStore.getState().initStore() // Initialize auth store and Token Coockies on module load

// GraphQL and Router Provider component
export function GraphqlRouterProvider(): ReactElement {
  const auth = useAuthStore()

  return (
    <UrqlProvider value={queryClient}>
      <RouterProvider
        router={router}
        context={{
          auth,
          queryClient,
        }}
      />
    </UrqlProvider>
  )
}
